import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaskProps } from 'models/tasks.types';

export type QaCacheScope = 'appointments' | 'renewals' | 'tasks';

export interface QaCacheEntry {
  fetchedAt: number;
  mostRecentCompletedAt: string | null;
  scope: QaCacheScope;
  tasks: TaskProps[];
  totalCount: number;
}

export interface QaCacheState {
  entries: Record<string, QaCacheEntry>;
  lastSyncAttempt: Record<string, number>;
  syncErrors: Record<string, number>;
}

const MAX_CACHE_BYTES = 30 * 1024 * 1024;

const buildKey = (date: string, scope: QaCacheScope): string => `${date}|${scope}`;

/**
 * Rough byte-size estimate of a cache entry.
 *
 * Why:
 *   We cap total cache at ~30MB to prevent runaway memory. JSON.stringify
 *   on every write is too expensive; this heuristic counts string-field
 *   bytes and per-task overhead instead.
 */
const estimateEntryBytes = (entry: QaCacheEntry): number => {
  const PER_TASK_OVERHEAD = 800;
  return entry.tasks.length * PER_TASK_OVERHEAD;
};

/**
 * Evicts oldest entries (by fetchedAt) until total size is under ceiling.
 */
const evictIfNeeded = (entries: Record<string, QaCacheEntry>): void => {
  const evictionOrder = Object.entries(entries)
    .map(([key, entry]) => ({
      fetchedAt: entry.fetchedAt,
      key,
      size: estimateEntryBytes(entry)
    }))
    .sort((left, right) => left.fetchedAt - right.fetchedAt);

  let totalBytes = evictionOrder.reduce((total, entry) => total + entry.size, 0);
  let index = 0;

  while (totalBytes > MAX_CACHE_BYTES && index < evictionOrder.length) {
    const entryToEvict = evictionOrder[index];
    delete entries[entryToEvict.key];
    totalBytes -= entryToEvict.size;
    index += 1;
  }
};

const initialState: QaCacheState = {
  entries: {},
  lastSyncAttempt: {},
  syncErrors: {}
};

const qaCacheSlice = createSlice({
  initialState,
  name: 'qaCache',
  reducers: {
    clearQaCache: () => initialState,

    invalidateAll: (state) => {
      state.entries = {};
      state.lastSyncAttempt = {};
      state.syncErrors = {};
    },

    invalidateByDate: (state, action: PayloadAction<string>) => {
      const date = action.payload;
      const keysToDelete = Object.keys(state.entries).filter((k) => k.startsWith(`${date}|`));
      for (const key of keysToDelete) {
        delete state.entries[key];
        delete state.lastSyncAttempt[key];
        delete state.syncErrors[key];
      }
    },

    invalidateByScope: (state, action: PayloadAction<QaCacheScope>) => {
      const scope = action.payload;
      const keysToDelete = Object.keys(state.entries).filter((k) => k.endsWith(`|${scope}`));
      for (const key of keysToDelete) {
        delete state.entries[key];
        delete state.lastSyncAttempt[key];
        delete state.syncErrors[key];
      }
    },

    mergeDelta: (
      state,
      action: PayloadAction<{
        date: string;
        newTasks: TaskProps[];
        scope: QaCacheScope;
        totalCount: number;
      }>
    ) => {
      const { date, newTasks, scope, totalCount } = action.payload;
      const key = buildKey(date, scope);
      const existing = state.entries[key];
      if (!existing) return;

      const existingIds = new Set(existing.tasks.map((t) => t._id));
      const dedupedNew = newTasks.filter((t) => !existingIds.has(t._id));
      existing.tasks = [...dedupedNew, ...existing.tasks];
      existing.totalCount = totalCount;
      existing.fetchedAt = Date.now();

      if (dedupedNew.length > 0) {
        const latestCompletedAt = dedupedNew.reduce((latest, t) => {
          if (!t.completedAt) return latest;
          return !latest || t.completedAt > latest ? t.completedAt : latest;
        }, existing.mostRecentCompletedAt);
        existing.mostRecentCompletedAt = latestCompletedAt;
      }

      evictIfNeeded(state.entries);
    },

    recordSyncAttempt: (state, action: PayloadAction<{ date: string; scope: QaCacheScope }>) => {
      const key = buildKey(action.payload.date, action.payload.scope);
      state.lastSyncAttempt[key] = Date.now();
    },

    recordSyncError: (state, action: PayloadAction<{ date: string; scope: QaCacheScope }>) => {
      const key = buildKey(action.payload.date, action.payload.scope);
      state.syncErrors[key] = Date.now();
    },

    setCacheEntry: (
      state,
      action: PayloadAction<{
        date: string;
        scope: QaCacheScope;
        tasks: TaskProps[];
        totalCount: number;
      }>
    ) => {
      const { date, scope, tasks, totalCount } = action.payload;
      const key = buildKey(date, scope);

      const mostRecentCompletedAt = tasks.reduce<string | null>((latest, t) => {
        if (!t.completedAt) return latest;
        return !latest || t.completedAt > latest ? t.completedAt : latest;
      }, null);

      state.entries[key] = {
        fetchedAt: Date.now(),
        mostRecentCompletedAt,
        scope,
        tasks,
        totalCount
      };

      evictIfNeeded(state.entries);
    }
  }
});

export const {
  clearQaCache,
  invalidateAll,
  invalidateByDate,
  invalidateByScope,
  mergeDelta,
  recordSyncAttempt,
  recordSyncError,
  setCacheEntry
} = qaCacheSlice.actions;

export default qaCacheSlice.reducer;
